import { useEffect, useState } from "react";

interface Shape {
  id: number;
  type: "cube" | "sphere" | "ring";
  x: number;
  y: number;
  size: number;
  rotateX: number;
  rotateY: number;
  rotateZ: number;
  delay: number;
  duration: number;
}

export function FloatingShapes() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };
    
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const shapes: Shape[] = [
    { id: 1, type: "cube", x: 10, y: 20, size: 60, rotateX: 45, rotateY: 45, rotateZ: 0, delay: 0, duration: 20 },
    { id: 2, type: "ring", x: 85, y: 15, size: 80, rotateX: 60, rotateY: 0, rotateZ: 30, delay: 2, duration: 25 },
    { id: 3, type: "sphere", x: 90, y: 70, size: 50, rotateX: 0, rotateY: 0, rotateZ: 0, delay: 1, duration: 15 },
    { id: 4, type: "cube", x: 5, y: 75, size: 40, rotateX: 30, rotateY: 60, rotateZ: 15, delay: 3, duration: 22 },
    { id: 5, type: "ring", x: 15, y: 50, size: 55, rotateX: 75, rotateY: 20, rotateZ: 0, delay: 1.5, duration: 18 },
  ];

  const renderShape = (shape: Shape) => {
    const baseStyle: React.CSSProperties = {
      position: "absolute",
      left: `${shape.x}%`,
      top: `${shape.y}%`,
      width: shape.size,
      height: shape.size,
      transform: `perspective(500px) rotateX(${shape.rotateX}deg) rotateY(${shape.rotateY}deg) rotateZ(${shape.rotateZ}deg)`,
      animation: prefersReducedMotion ? "none" : `float3d ${shape.duration}s ease-in-out infinite`,
      animationDelay: `${shape.delay}s`,
    };

    if (shape.type === "cube") {
      return (
        <div key={shape.id} style={baseStyle} className="preserve-3d">
          <div
            className="absolute inset-0 border border-primary/20 rounded-lg bg-primary/5"
            style={{ transform: "translateZ(15px)" }}
          />
          <div
            className="absolute inset-0 border border-primary/10 rounded-lg bg-primary/3"
            style={{ transform: "translateZ(-15px)" }}
          />
        </div>
      );
    }

    if (shape.type === "ring") {
      return (
        <div
          key={shape.id}
          style={baseStyle}
          className="rounded-full border-2 border-primary/20 bg-transparent"
        />
      );
    }

    // Sphere
    return (
      <div
        key={shape.id}
        style={baseStyle}
        className="rounded-full bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/15 shadow-lg"
      />
    );
  };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map(renderShape)}
    </div>
  );
}
